import { STRANDS_BY_GRADE } from '../../../shared/constant/strands';

// Column indices for the learner table in DepEd's School Form 1 (SF1/SF1-SHS) templates.
const COL = {
  LRN: 0,
  NAME: 2,
  BARANGAY: 25,
  TOWN: 30,
  PROVINCE: 32,
  MOTHER_NAME: 41,
};

const STRAND_KEYWORDS = {
  ICT: ['computer', 'ict', 'programming', 'hardware servicing', 'animation', 'illustration'],
  HE: ['cookery', 'bread and pastry', 'housekeeping', 'food and beverage', 'dressmaking', 'cosmetology', 'caregiving'],
  HAT: ['tourism', 'front office', 'bartending', 'travel services', 'hospitality'],
  ABM: ['accountancy', 'business', 'entrepreneurship', 'abm'],
  HUMSS: ['humanities and social', 'humss'],
  ASSH: ['arts, social sciences', 'assh'],
};

function capitalizeWords(value) {
  return value
    .toLowerCase()
    .replace(/(^|[\s(])([a-z])/g, (_, boundary, letter) => boundary + letter.toUpperCase());
}

const ROMAN_NUMERALS = new Set(['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII']);

function formatBarangay(raw) {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  return value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => {
      const bare = word.toUpperCase().replace(/[().]/g, '');
      if (ROMAN_NUMERALS.has(bare)) return word.toUpperCase();
      return capitalizeWords(word);
    })
    .join(' ');
}

function findLabelValue(rows, labelMatcher, { nextRow = false } = {}) {
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    for (let c = 0; c < row.length; c++) {
      const cell = String(row[c] ?? '').trim();
      if (!cell || !labelMatcher(cell)) continue;

      if (nextRow) {
        const below = rows[r + 1] || [];
        const val = below.find((v) => String(v ?? '').trim());
        return val ? String(val).trim() : '';
      }
      for (let c2 = c + 1; c2 < row.length; c2++) {
        const val = String(row[c2] ?? '').trim();
        if (val) return val;
      }
    }
  }
  return '';
}

// "Section" and its value share a row with the TVL course/specialization, e.g.
// ['Section', 'AAG', 'Computer Hardware Servicing (NC II), ...'] — course is the
// next non-empty cell after the section value.
function findSectionAndCourse(rows) {
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    const labelIdx = row.findIndex((cell) => /^section$/i.test(String(cell ?? '').trim()));
    if (labelIdx === -1) continue;

    let section = '';
    let course = '';
    for (let c = labelIdx + 1; c < row.length; c++) {
      const val = String(row[c] ?? '').trim();
      if (!val) continue;
      if (!section) section = val;
      else if (!course) course = val;
      else break;
    }
    return { section, course };
  }
  return { section: '', course: '' };
}

// The learner's middle name is normally the mother's maiden surname, which can be
// more than one word (e.g. "Del Rosario"), so a trailing DepEd "-" (no middle name
// on file — common when the child carries the mother's surname and no father is on
// record) is honored as-is, and otherwise the mother's maiden surname column is used
// to find where the given name ends, rather than assuming the middle name is one word.
function splitFullName(raw, motherMaidenRaw) {
  const value = String(raw ?? '').trim();
  if (!value) return { lastname: '', firstname: '', middlename: '' };

  const [lastPart, ...rest] = value.split(',');
  const lastname = lastPart.trim();
  let remainder = rest.join(',').trim();
  const hasNoMiddleMarker = /-\s*$/.test(remainder);
  remainder = remainder.replace(/-+\s*$/, '').trim();
  if (!remainder) return { lastname, firstname: '', middlename: '' };

  if (hasNoMiddleMarker) {
    return { lastname, firstname: remainder, middlename: '' };
  }

  const maidenSurname = String(motherMaidenRaw ?? '').split(',')[0]?.trim() ?? '';
  if (maidenSurname && remainder.toUpperCase().endsWith(maidenSurname.toUpperCase())) {
    const firstname = remainder.slice(0, remainder.length - maidenSurname.length).trim();
    if (firstname) return { lastname, firstname, middlename: maidenSurname };
  }

  const tokens = remainder.split(/\s+/);
  if (tokens.length === 1) return { lastname, firstname: tokens[0], middlename: '' };

  const middlename = tokens.pop();
  return { lastname, firstname: tokens.join(' '), middlename };
}

function parseMotherName(raw) {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  const parts = value.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  const [last, first] = parts;
  return first ? `${capitalizeWords(first)} ${capitalizeWords(last)}` : capitalizeWords(last);
}

function detectStrand(gradeLevel, track, course) {
  const options = STRANDS_BY_GRADE[gradeLevel] ?? [];
  if (options.length === 0) return '';

  const haystack = `${track} ${course}`.toLowerCase();
  for (const [code, keywords] of Object.entries(STRAND_KEYWORDS)) {
    if (!options.some((o) => o.code === code)) continue;
    if (keywords.some((kw) => haystack.includes(kw))) return code;
  }
  return '';
}

export function isDepedSf1Sheet(rows) {
  return rows
    .slice(0, 5)
    .some((row) => row.some((cell) => String(cell ?? '').toLowerCase().includes('school form 1')));
}

export function extractDepedSf1(rows) {
  const gradeLevel = findLabelValue(rows, (c) => /^grade level/i.test(c));
  const schoolYearRaw = findLabelValue(rows, (c) => /^school year/i.test(c));
  const schoolYear = schoolYearRaw.replace(/\s*-\s*/g, '-');
  const track = findLabelValue(rows, (c) => /^track and strand/i.test(c), { nextRow: true });
  const { section, course } = findSectionAndCourse(rows);

  const detectedStrand = detectStrand(gradeLevel, track, course);

  const headerRowIdx = rows.findIndex((row) => String(row[COL.LRN] ?? '').trim().toUpperCase() === 'LRN');
  const dataStart = headerRowIdx >= 0 ? headerRowIdx + 2 : rows.length;

  const rawRows = [];
  for (let r = dataStart; r < rows.length; r++) {
    const row = rows[r];
    const lrnCell = String(row[COL.LRN] ?? '').replace(/\D/g, '');
    const nameCell = String(row[COL.NAME] ?? '').trim();

    if (!nameCell) break;
    if (nameCell.startsWith('<===') || /\b(total|combined)\b/i.test(nameCell)) continue;

    const { lastname, firstname, middlename } = splitFullName(nameCell, row[COL.MOTHER_NAME]);
    rawRows.push({
      lrn: lrnCell,
      lastname,
      firstname,
      middlename,
      grade_level: gradeLevel,
      section,
      strand: detectedStrand,
      guardian_name: parseMotherName(row[COL.MOTHER_NAME]),
      province: String(row[COL.PROVINCE] ?? '').trim(),
      town: String(row[COL.TOWN] ?? '').trim(),
      brgy: formatBarangay(row[COL.BARANGAY]),
      school_year: schoolYear || null,
      status: 'Active',
    });
  }

  return {
    meta: {
      gradeLevel,
      section,
      schoolYear,
      track,
      course,
      detectedStrand,
      requiresStrand: (STRANDS_BY_GRADE[gradeLevel] ?? []).length > 0,
    },
    rawRows,
  };
}
